import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Trophy, ChevronRight, Zap } from "lucide-react";
import type { PhaseStats } from "@/types/api.types";

interface PhaseOverviewProps {
  stats: PhaseStats;
  onAdvance: () => void;
  onSimulate: () => void;
  isAdvancing: boolean;
  isSimulating: boolean;
}

const formatPhase = (phase: string) =>
  phase.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export default function PhaseOverview({
  stats,
  onAdvance,
  onSimulate,
  isAdvancing,
  isSimulating,
}: PhaseOverviewProps) {
  return (
    <div className="bg-white rounded-xl shadow-sm border p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-100 rounded-lg">
            <Trophy className="h-6 w-6 text-blue-600" />
          </div>
          <div>
            <p className="text-sm text-gray-500">Current Phase</p>
            <h2 className="text-2xl font-bold text-gray-900">
              {formatPhase(stats.currentPhase)}
            </h2>
          </div>
        </div>

        <div className="flex gap-2">
          {stats.canAdvance && stats.nextPhase && (
            <>
              <Button
                variant="outline"
                onClick={onSimulate}
                disabled={isSimulating}
                className="text-orange-600 border-orange-300 hover:bg-orange-50"
              >
                <Zap className="h-4 w-4 mr-2" />
                {isSimulating ? "Simulating..." : "Auto Advance"}
              </Button>
              <Button
                onClick={onAdvance}
                disabled={isAdvancing}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                <ChevronRight className="h-4 w-4 mr-2" />
                {isAdvancing
                  ? "Advancing..."
                  : `Advance to ${formatPhase(stats.nextPhase)}`}
              </Button>
            </>
          )}

          {!stats.canAdvance && (
            <div className="flex flex-col items-end gap-1">
              <Badge className="bg-yellow-100 text-yellow-800 px-3 py-1.5">
                ⏳ Cannot advance yet
              </Badge>
              <div className="text-xs text-gray-500 text-right space-y-0.5">
                {stats.liveMatches > 0 && (
                  <p className="text-green-600 font-medium">{stats.liveMatches} match(es) still LIVE</p>
                )}
                {stats.scheduledMatches > 0 && (
                  <p className="text-gray-600">{stats.scheduledMatches} match(es) still SCHEDULED</p>
                )}
                <p>{stats.completedMatches}/{stats.totalMatches} matches completed in {formatPhase(stats.currentPhase)}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Progress Bar */}
      <div className="mb-6">
        <div className="flex justify-between text-sm text-gray-600 mb-2">
          <span>Phase Progress</span>
          <span>
            {stats.completedMatches} / {stats.totalMatches} matches completed
          </span>
        </div>
        <div className="w-full bg-gray-100 rounded-full h-3">
          <div
            className="bg-blue-600 h-3 rounded-full transition-all duration-500"
            style={{ width: `${stats.progressPercentage}%` }}
          />
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          label="Live Matches"
          value={stats.liveMatches}
          color="green"
          icon="🔴"
        />
        <StatCard
          label="Scheduled"
          value={stats.scheduledMatches}
          color="gray"
          icon="📅"
        />
        <StatCard
          label="Active Nations"
          value={stats.activeNations}
          color="blue"
          icon="🏳️"
        />
        <StatCard
          label="Eliminated"
          value={stats.eliminatedNations}
          color="red"
          icon="❌"
        />
      </div>
    </div>
  );
}

function StatCard({
  label,
  value,
  color,
  icon,
}: {
  label: string;
  value: number;
  color: string;
  icon: string;
}) {
  const colors: Record<string, string> = {
    green: "bg-green-50 text-green-700",
    gray: "bg-gray-50 text-gray-700",
    blue: "bg-blue-50 text-blue-700",
    red: "bg-red-50 text-red-700",
  };

  return (
    <div className={`rounded-lg p-4 ${colors[color]}`}>
      <p className="text-2xl font-bold">
        {icon} {value}
      </p>
      <p className="text-sm mt-1 opacity-80">{label}</p>
    </div>
  );
}
